import { zodResolver } from '@hookform/resolvers/zod';
import { signInResultSchema } from '@wodi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Button, Notice, SegmentedControl, TextField } from '@wodi/ui';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { api } from '../api/client.ts';
import { safeNext, sessionQuery } from '../api/session.ts';
import { errorText, isLanguage, useI18n, type Translate } from '../i18n/i18n.tsx';
import styles from './sign-in.module.css';

const route = getRouteApi('/sign-in');

const formSchemaIn = (t: Translate) =>
  z.object({
    email: z.email({ error: t('signIn.emailInvalid') }),
    password: z.string().min(1, t('signIn.passwordMissing')),
  });

type FormValues = z.infer<ReturnType<typeof formSchemaIn>>;

// Idle timeout deletes the retry queue with every draft (ADR 0018); never silently.
const REASON_KEYS = {
  expired: 'signIn.reason.expired',
  expired_unsent: 'signIn.reason.expired_unsent',
  signed_out: 'signIn.reason.signed_out',
} as const;

export function SignInPage() {
  const { next, reason } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, language, setLanguage } = useI18n();
  const formSchema = useMemo(() => formSchemaIn(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
  });

  const signIn = useMutation({
    mutationFn: (values: FormValues) =>
      api('POST', '/auth/sign-in', { body: values, schema: signInResultSchema }),
    onSuccess: async ({ next: step }) => {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      const search = { next: safeNext(next) };
      if (step === 'two_factor') await navigate({ to: '/sign-in/code', search });
      else if (step === 'two_factor_enrolment')
        await navigate({ to: '/sign-in/set-up-2fa', search });
      else await navigate({ to: safeNext(next) });
    },
    onError: () => {
      form.setValue('password', '');
    },
  });

  const error = signIn.error === null ? null : errorText(t, language, signIn.error);

  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <div className={styles.brand}>Wodi</div>
        <h1 className={styles.title}>{t('signIn.title')}</h1>
        <SegmentedControl
          label={t('language.label')}
          options={[
            { id: 'en', label: t('language.en') },
            { id: 'sw', label: t('language.sw') },
          ]}
          value={language}
          onChange={(id) => {
            if (isLanguage(id)) setLanguage(id);
          }}
        />
        {reason !== undefined && error === null ? (
          <Notice tone="info">{t(REASON_KEYS[reason])}</Notice>
        ) : null}
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            void form.handleSubmit((values) => {
              signIn.mutate(values);
            })(event);
          }}
        >
          <Controller
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <TextField
                label={t('signIn.email')}
                type="email"
                autoComplete="username"
                autoFocus
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                {...(fieldState.error?.message === undefined
                  ? {}
                  : { errorMessage: fieldState.error.message })}
              />
            )}
          />
          <Controller
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <TextField
                label={t('signIn.password')}
                type="password"
                autoComplete="current-password"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                {...(fieldState.error?.message === undefined
                  ? {}
                  : { errorMessage: fieldState.error.message })}
              />
            )}
          />
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isPending={signIn.isPending}
              pendingLabel={t('signIn.pending')}
            >
              {t('signIn.submit')}
            </Button>
          </div>
        </form>
        <p className={styles.lead}>{t('signIn.forgot')}</p>
      </div>
    </main>
  );
}
